/* eslint-disable camelcase */
import { ISignUpResult } from 'amazon-cognito-identity-js'
import { path as ramdaPath } from 'ramda'
import { isMobile } from 'react-device-detect'
import { toast } from 'react-toastify'
import { SubmissionError } from 'redux-form'
import { SagaIterator } from 'redux-saga'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { openDialog } from '../actions/dialogs'
import { initializeIot } from '../actions/iot'
import { notifyAdminLoginFail } from '../services/users'
import {
  changePassword,
  changePasswordFail,
  changePasswordSuccess,
  confirmPasswordReset,
  confirmPasswordResetFail,
  confirmPasswordResetSuccess,
  initializeSession,
  initializeSessionFail,
  initializeSessionSuccess,
  login,
  loginFail,
  loginSuccess,
  logout,
  logoutFail,
  logoutSuccess,
  saveLastUrl,
  sendPasswordResetCode,
  sendPasswordResetCodeFail,
  sendPasswordResetCodeSuccess,
  signup,
  signupFail,
  signupSuccess,
  updateUserInformation,
  updateUserInformationFail,
  updateUserInformationSuccess,
  setDeviceType,
  listUsersFail,
  listUsersSuccess,
  listUsers,
  updateUserSuccess,
  getCurrentUserSuccess,
  getCurrentUserFail,
  getCurrentUser,
  updateUserFail,
  updateUser,
  signupOnApartmentConfirmationSuccess,
  signupOnApartmentConfirmationFail,
  getUser,
  getUserSuccess,
  getUserFail,
  resendActivationEmail
} from '../actions/users'
import { addFormSubmit } from '../config/googleTagManager'
import { formatPhoneNumForCognito } from '../libs/conversions'
import { isLanguageString } from '../libs/language'
import { getLowerCaseString } from '../libs/strings'
import { Action } from '../reducers/types'
import { selectLocationPathname } from '../selectors/router'
import {
  selectLastUrl,
  selectUserAttributes,
  selectUserId
} from '../selectors/users'
import {
  authChangePassword,
  authForgotPassword,
  authForgotPasswordSubmit,
  authLogin,
  authLogout,
  authSignup,
  authUpdateUserInfo,
  getUserInfoFromSession,
  authLoginErrorCodes,
  authResendActivationEmail
} from '../services/auth/auth'
import {
  UserInfoFromSession,
  UserAttributes,
  SignupProps
} from '../services/auth/types'
import UsersApi from '../services/users'
import { getHistory } from '../store'
import i18n, { changeLanguage, getCurrentLanguage } from '../translations/i18n'
import { PayloadParameters } from '../types/payloads'
import { User, UpdateUserData, isUserWithContactInfo } from '../types/users'
import { validateIsAdmin } from '../utils/validation/users'

interface Parameters<T> {
  payload: {
    formData: T
    resolve: any
    reject: any
  }
}

// paths that do not require authentication
const unAuthPaths = [
  '/confirm',
  '/new-application',
  '/forgotpassword/reset',
  '/forgotpassword/request',
  '/reserve',
  '/signup',
  '/register',
  '/create/apartment',
  //'/instant-rent',
  '/aftershowing',
  '/tenants'
]

/**
 * Allow unauthenticated paths with and without ending slash
 * such as "/signup" and "/signup/". "currentPath" does not include query parameters.
 */
const isPathValid = (currentPath: string) =>
  unAuthPaths.some(
    (unAuthPath) =>
      currentPath === '/' ||
      currentPath.startsWith(unAuthPath) ||
      `${unAuthPath}/` === currentPath
  )

const handleLanguageChange = (userCognitoData: UserInfoFromSession) => {
  const language = ramdaPath(['attributes', 'custom:language'], userCognitoData)
  const langDefined = isLanguageString(language)
    ? (language as 'fi' | 'en')
    : false
  if (langDefined) {
    changeLanguage(langDefined)
  }
}

function* handleInitializeSession(): SagaIterator {
  try {
    yield put(setDeviceType(isMobile))
    const userCognitoData: UserInfoFromSession = yield call(
      getUserInfoFromSession
    )
    if (!userCognitoData) {
      throw new Error('invalid user session')
    }
    yield call(UsersApi.initIot) // keep IoT policies updated
    handleLanguageChange(userCognitoData)
    const userId = userCognitoData.sub
    let user: User = yield call(UsersApi.getUser, userId)
    if (user && !isUserWithContactInfo(user)) {
      /** only used temporarily while not all accounts have loginEmail */
      const {
        attributes: {
          email: loginEmail,
          phone_number: phoneNumber,
          'custom:language': language,
          name
        }
      } = userCognitoData

      const userData: UpdateUserData = {
        loginEmail,
        data: { language, phoneNumber, name }
      }
      user = yield call(UsersApi.updateUser, userId, userData)
    }
    if (user && user.userId) {
      yield put(getCurrentUserSuccess(user))
    }
    // currently, IoT Websockets are only supported for admin users
    if (userCognitoData.role === 'admin') {
      yield put(initializeIot())
    }
    yield put(initializeSessionSuccess(userCognitoData))
  } catch (error) {
    yield put(initializeSessionFail(error.message))
    const currentPath = yield select(selectLocationPathname)
    if (!isPathValid(currentPath)) {
      yield put(saveLastUrl(currentPath))
      yield call(getHistory().push, '/' as any)
    }
  }
}

type HandleSignUpParameters = Parameters<{
  email: string
  password: string
  phoneNumber: string
  name: string
}>

function* handleSignup({
  payload: { formData, resolve, reject }
}: HandleSignUpParameters): SagaIterator {
  try {
    const { email, name, phoneNumber: rawPhoneNum, password } = formData
    const emailLowerChar = getLowerCaseString(email)
    const phoneNumber = formatPhoneNumForCognito(rawPhoneNum)
    const language = getCurrentLanguage()
    const signupInfo: SignupProps = {
      name,
      email: emailLowerChar,
      phone_number: phoneNumber,
      password,
      'custom:language': language
    }

    yield call(authSignup, signupInfo)
    yield put(signupSuccess(signupInfo.email))
    yield put(openDialog('SignupSuccess'))
    addFormSubmit('signup')
    resolve()
  } catch (error) {
    yield put(signupFail({ error: error.message }))
    const _error = i18n.t(
      error.code === 'UsernameExistsException'
        ? `signup.${error.code}`
        : 'signup.signupFailed'
    )
    yield call(reject, new SubmissionError({ _error }))
  }
}

interface SignupWithApartmentConfirmation {
  email: string
  name: string
  password: string
  phoneNumber: string
  isSVAMember: boolean
}

/** do not connect to takeLatest so not callable from redux-devtools */
export function* handleSignupOnApartmentConfirmation({
  email,
  phoneNumber: rawPhoneNum,
  password,
  name,
  isSVAMember
}: SignupWithApartmentConfirmation): SagaIterator {
  const emailLowerChar = getLowerCaseString(email)
  const phoneNumber = formatPhoneNumForCognito(rawPhoneNum)
  let loginResult:
    | undefined
    | 'successfulLogin'
    | 'UserNotFoundException'
    | 'UserNotConfirmedException'
    | 'NotAuthorizedException'
    | 'DuplicateNumberException'
    | 'otherError'
  try {
    yield call(authLogin, emailLowerChar, password)
    yield call(handleInitializeSession)
    yield put(loginSuccess())
    loginResult = 'successfulLogin'
    /** save user t */
  } catch (error) {
    loginResult = error.code
  }
  /** new user, most common scenario, create user and send confirmation */
  if (loginResult === 'UserNotFoundException') {

    const language = getCurrentLanguage()

    // Check for duplicate phone number
    // const isPhoneNumberDuplicateFetch = yield call(checkForDuplicateRegistrations, phoneNumber, email);
    // if (isPhoneNumberDuplicateFetch) {
    //   const isPhoneNumberDuplicate = JSON.parse(isPhoneNumberDuplicateFetch.body);
    //   if (isPhoneNumberDuplicate.isDuplicate === true) {
    //     throw new Error('DuplicateNumberException')
    //   }
    // }

    const signupInfo: SignupProps = {
      email: emailLowerChar,
      phone_number: phoneNumber,
      password,
      name,
      'custom:language': language,
      isSVAMember
    }
    const { userSub: userId }: ISignUpResult = yield call(
      authSignup,
      signupInfo
    )
    yield put(signupSuccess(signupInfo.email))
    yield put(signupOnApartmentConfirmationSuccess(userId))
    addFormSubmit('signup')
  } else if (loginResult === 'UserNotConfirmedException') {
    /** todo user has not confirmed email, send new confirmation automatically? */
    yield put(signupOnApartmentConfirmationFail())
    throw new Error('UserNotConfirmedException')
  } else if (loginResult === 'NotAuthorizedException') {
    /** todo email exists, but user has wrong password. */
    yield call(notifyAdminLoginFail, emailLowerChar, phoneNumber)
    throw new Error('NotAuthorizedException')
  }
}

type HandleLoginParameters = Parameters<{
  email: string
  password: string
}>

function* handleLogIn({
  payload: { formData, resolve, reject }
}: HandleLoginParameters): SagaIterator {
  try {
    yield call(
      authLogin,
      getLowerCaseString(formData.email),
      formData.password
    )
    yield call(handleInitializeSession)
    yield put(loginSuccess())
    const lastUrl = yield select(selectLastUrl)
    if (lastUrl) {
      yield call(getHistory().push, lastUrl as any)
    } else {
      yield call(getHistory().push, '/apartments' as any)
    }
    addFormSubmit('login')
    resolve()
  } catch (error) {
    yield put(
      loginFail(
        error.code === 'UserNotConfirmedException' ? formData.email : null
      )
    )
    const _error = i18n.t(authLoginErrorCodes(error.code))
    yield call(reject, new SubmissionError({ _error }))
  }
}

function* handleResendActivationEmail({
  payload: notSelectedEmail
}: {
  payload: string
}): SagaIterator {
  try {
    yield call(authResendActivationEmail, notSelectedEmail)
    toast.info(i18n.t('login.resendActivationEmailSuccess'))
  } catch (error) {
    toast.info(i18n.t('login.resendActivationEmailFail'))
  }
}

function* handleLogout(): SagaIterator {
  try {
    yield call(authLogout)
    yield put(logoutSuccess())
    yield call(getHistory().push, '/' as any)
    window.location.reload() // force clean state.
  } catch (error) {
    yield put(logoutFail())
  }
}

/** only admin users can get other users by userId */
export function* handleGetUser({
  payload: userId
}: Action<string>): SagaIterator {
  try {
    const user: User = yield call(UsersApi.getUser, userId)
    yield put(getUserSuccess(user))
  } catch (error) {
    yield put(getUserFail(error.message))
  }
}

type HandleUpdateUserInformationParameters = Parameters<{
  name: string
  phone_number: any
  'custom:language': 'fi' | 'en'
}>

/** Updates cognito userAttributes and users dynamoDB */
export function* handleUpdateUserInformation({
  payload: { formData, resolve, reject }
}: HandleUpdateUserInformationParameters): SagaIterator {
  try {
    const {
      'custom:language': language,
      phone_number: phoneNumberUnhandled,
      name
    } = formData
    const userId = yield select(selectUserId)
    const phoneNumber = formatPhoneNumForCognito(
      phoneNumberUnhandled.phone || phoneNumberUnhandled
    )

    const userAttributes: UserAttributes = yield select(selectUserAttributes)
    /** only used temporarily while not all accounts have loginEmail */
    const { email: loginEmail } = userAttributes

    // Check for duplicate phone number
    // const isPhoneNumberDuplicateFetch = yield call(checkForDuplicatePhoneNumber, phoneNumber, loginEmail);
    // if (isPhoneNumberDuplicateFetch) {
    //   const isPhoneNumberDuplicate = JSON.parse(isPhoneNumberDuplicateFetch.body);
    //   if (isPhoneNumberDuplicate.isDuplicate === true) {
    //     yield put(updateUserInformationFail("duplicate phonenumber error"))
    //     yield call(
    //       reject,
    //       new SubmissionError({
    //         _error: i18n.t('error.user.duplicatePhoneNumber')
    //       })
    //     )
    //     return
    //   }
    // }

    const formDataWithCorrectPhone = {
      ...formData,
      phone_number: phoneNumber
    }
    yield call(authUpdateUserInfo, formDataWithCorrectPhone)

    const userData = {
      loginEmail,
      data: { language: language, phoneNumber, name }
    }
    changeLanguage(language)

    const updatedUser: User = yield call(UsersApi.updateUser, userId, userData)

    yield put(updateUserInformationSuccess(formDataWithCorrectPhone))
    yield put(updateUserSuccess(updatedUser))

    toast.info(i18n.t('profile.toast.profileInfoUpdated'))
    addFormSubmit('profile')
    resolve()
  } catch (error) {
    yield put(updateUserInformationFail(error.message))
    yield call(
      reject,
      new SubmissionError({
        _error: i18n.t('error.user.couldNotUpdate')
      })
    )
  }
}

/** Update user DB file */
export function* handleUpdateUser({
  payload: { userId, userData }
}: PayloadParameters<{
  userId: string
  userData: UpdateUserData
}>): SagaIterator {
  try {
    const updatedUser: User = yield call(UsersApi.updateUser, userId, userData)
    yield put(updateUserSuccess(updatedUser))
  } catch (error) {
    yield put(updateUserFail(error.message))
  }
}

type HandleChangePasswordParameters = Parameters<{
  currentPassword: string
  password: string
}>

export function* handleChangePassword({
  payload: {
    formData: { currentPassword, password },
    resolve,
    reject
  }
}: HandleChangePasswordParameters): SagaIterator {
  try {
    yield call(authChangePassword, currentPassword, password)
    toast.info(i18n.t('profile.toast.passwordChanged'))
    yield put(changePasswordSuccess())
    resolve()
  } catch (error) {
    yield put(changePasswordFail(error.code))
    yield call(
      reject,
      new SubmissionError({
        _error: i18n.t('error.user.somethingWrongPwChange')
      })
    )
  }
}

type handleSendPasswordResetCodeParameters = Parameters<{
  email: string
}>

export function* handleSendPasswordResetCode({
  payload: {
    formData: { email },
    resolve,
    reject
  }
}: handleSendPasswordResetCodeParameters): SagaIterator {
  try {
    yield call(authForgotPassword, getLowerCaseString(email))
    yield put(sendPasswordResetCodeSuccess())
    addFormSubmit('requestPasswordChange')
    resolve()
  } catch (error) {
    yield put(sendPasswordResetCodeFail(error.code))
    yield call(
      reject,
      new SubmissionError({
        _error: i18n.t('error.user.codeNotSent')
      })
    )
  }
}

type handleConfirmPasswordResetParameters = Parameters<{
  email: string
  verificationCode: string
  password: string
}>

export function* handleConfirmPasswordReset({
  payload: {
    formData: { email, verificationCode, password },
    resolve,
    reject
  }
}: handleConfirmPasswordResetParameters): SagaIterator {
  try {
    yield call(authForgotPasswordSubmit, getLowerCaseString(email), verificationCode, password)
    toast.info(i18n.t('profile.toast.passwordChanged'))
    yield put(confirmPasswordResetSuccess())
    yield call(getHistory().push, '/' as any)
    addFormSubmit('passwordChanged')
    resolve()
  } catch (error) {
    yield put(confirmPasswordResetFail(error.code))
    yield call(
      reject,
      new SubmissionError({
        _error: i18n.t('error.user.somethingWrongPwChange')
      })
    )
  }
}

export function* handleGetCurrentUser(): SagaIterator {
  try {
    const userId: string | undefined = yield select(selectUserId)
    if (!userId) {
      throw new Error('userId missing')
    }
    const user: User = yield call(UsersApi.getUser, userId)

    // temporarily used to update user info to DB without refresh
    if (user && !isUserWithContactInfo(user)) {
      // initializeSession includes getCurrentUserSuccess
      yield put(initializeSession())
    } else if (!user) {
      throw new Error('user not found')
    } else {
      yield put(getCurrentUserSuccess(user))
    }
  } catch (error) {
    yield put(getCurrentUserFail(error.message))
  }
}

/** Only for admin users */
export function* handleListUsers(): SagaIterator {
  try {
    yield call(validateIsAdmin)
    const users: User[] = yield call(UsersApi.getUsers)
    yield put(listUsersSuccess(users))
  } catch (error) {
    yield put(listUsersFail(error.message))
  }
}

function* watchUsersActions(): SagaIterator {
  yield all([
    takeLatest(initializeSession, handleInitializeSession),
    takeLatest(signup, handleSignup),
    takeLatest(login, handleLogIn),
    takeLatest(resendActivationEmail, handleResendActivationEmail),
    takeLatest(logout, handleLogout),
    takeLatest(getUser, handleGetUser),
    takeLatest(updateUserInformation, handleUpdateUserInformation),
    takeLatest(updateUser, handleUpdateUser),
    takeLatest(changePassword, handleChangePassword),
    takeLatest(sendPasswordResetCode, handleSendPasswordResetCode),
    takeLatest(confirmPasswordReset, handleConfirmPasswordReset),
    takeLatest(listUsers, handleListUsers),
    takeLatest(getCurrentUser, handleGetCurrentUser)
  ])
}

const usersSagas = [watchUsersActions]

export default usersSagas
